/** Recruitment Network · candidate detail panel. A read-only summary of what the network already
 *  loaded; every decision (invite, advance, cut) stays on the existing Review / requirement pages,
 *  which the footer links to — this panel never duplicates that logic. */
import { useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, X, ArrowRight, FileText, AlertTriangle, ScrollText, ExternalLink, Copy, Check } from "lucide-react";
import { PIPELINE_STATUSES, PIPELINE_STATUS_LABEL } from "@talenthop/shared";
import type { CandidacyNote, PipelineStatus } from "@talenthop/shared";
import { api, ApiError } from "../../../lib/api.js";
import { ResumeProfileLines, hasResumeProfile } from "../ResumeProfileView.js";
import { initialsOf } from "../../../lib/initials.js";
import { displayTitle } from "../displayTitle.js";
import type { NetCandidate, NetRequisition, StageKey } from "./networkModel.js";
import { STAGES, inStage } from "./networkModel.js";
import { STATE_LABEL, ScoreRing, StatusBadge, scoreTone } from "./NetworkNodes.js";
import { CandidateDetailsFields } from "./CandidateDetailsFields.js";
import { CandidateTimeline } from "../../../components/CandidateTimeline.js";
import { Modal } from "../../admin/adminUi.js";

type Tab = "overview" | "analysis" | "interview" | "notes";

/**
 * The recruiter's own status tag (migration 0077) — the same PATCH and cache key the Requirement
 * page's status column uses, so both views stay in step. It is a LABEL for the team: it never
 * advances, rejects or notifies anyone (the Review page owns decisions).
 */
function StatusControl({ c, reqId }: { c: NetCandidate; reqId: string }): ReactNode {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (pipelineStatus: PipelineStatus | null) =>
      api.patch(`/requisitions/${reqId}/candidates/${c.candidacyId}`, { pipelineStatus }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["funnel", reqId] }),
  });
  // Only a candidacy carries the tag; a resume nobody has invited or added yet has nothing to set.
  if (!c.candidacyId) return null;
  return (
    <select
      className={`rn-status-pill${c.pipelineStatus ? ` tag-${c.pipelineStatus}` : ""}${save.isError ? " is-error" : ""}`}
      aria-label="Team status (a label for your team; doesn't notify the candidate)"
      data-tip={save.isError ? "Couldn’t save — try again" : "Team label · doesn’t notify the candidate"}
      value={c.pipelineStatus ?? ""}
      disabled={save.isPending}
      onChange={(e) => save.mutate((e.target.value || null) as PipelineStatus | null)}
    >
      <option value="">Set status</option>
      {PIPELINE_STATUSES.map((s) => (
        <option key={s} value={s}>{PIPELINE_STATUS_LABEL[s]}</option>
      ))}
    </select>
  );
}

/** Recruiter notes — the same shared thread (and cache key) as the Review page's notes panel. */
/**
 * The CV, opened in a new tab: GET …/resumes/{scanId}/file mints a 2-hour read link (audited).
 * The tab is opened synchronously on click and pointed at the link once it arrives — opening it
 * after the await would be swallowed by the browser's pop-up blocker.
 */
function ResumeLink({ reqId, scanId, filename }: { reqId: string; scanId: string; filename: string }): ReactNode {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const open = async () => {
    setErr(null);
    setBusy(true);
    const tab = window.open("about:blank", "_blank");
    try {
      const { url } = await api.get<{ url: string }>(`/requisitions/${reqId}/resumes/${scanId}/file`);
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.open(url, "_blank", "noopener");
      }
    } catch (e) {
      tab?.close();
      setErr(e instanceof ApiError ? e.message : "Couldn't open the CV — try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <p className="rn-contact">
        <FileText size={14} />
        <button type="button" className="rn-file-link" onClick={() => void open()} disabled={busy} data-tip="Open the CV in a new tab">
          {filename}
          <ExternalLink size={12} aria-hidden="true" />
        </button>
      </p>
      {err && <p className="rn-muted sm">{err}</p>}
    </>
  );
}

/**
 * "View report" for a resume that is only a CV on file: POST …/resumes/{scanId}/candidate makes it a
 * candidate (sourced; nobody is contacted or charged), then the report page opens.
 */
function ViewReportButton({ reqId, scanId }: { reqId: string; scanId: string | null }): ReactNode {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const open = useMutation({
    mutationFn: () => api.post<{ candidacyId: string }>(`/requisitions/${reqId}/resumes/${scanId}/candidate`, {}),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ["funnel", reqId] });
      void qc.invalidateQueries({ queryKey: ["reqScans", reqId] });
      void qc.invalidateQueries({ queryKey: ["org-candidates"] });
      navigate(`/app/review/${res.candidacyId}`);
    },
  });
  return (
    <>
      <button type="button" className="rn-cta" disabled={!scanId || open.isPending} onClick={() => open.mutate()}>
        {open.isPending ? "Opening…" : "View report"} <ArrowRight size={15} />
      </button>
      {open.isError && (
        <p className="rn-muted sm" style={{ margin: "6px 0 0" }}>
          {open.error instanceof ApiError ? open.error.message : "Couldn't open the report — try again."}
        </p>
      )}
    </>
  );
}

/** No CV on file: ask for it here — the same request (and email) as the Candidates page's Request CV. */
function RequestCv({ candidacyId, email }: { candidacyId: string; email: string | null }): ReactNode {
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const req = useMutation({
    mutationFn: () =>
      api.post<{ sentTo: string; expiresAt: string; link: string }>(`/candidacies/${candidacyId}/request-info`, {
        needsResume: true,
        needsProfile: true,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["candidacy-timeline", `/candidacies/${candidacyId}/timeline`] }),
  });
  if (!email) {
    return (
      <p className="rn-contact rn-muted">
        <FileText size={14} /> No CV yet — add an email address to request one.
      </p>
    );
  }
  if (req.data) {
    const link = req.data.link;
    return (
      <div className="rn-cv-requested">
        <p className="rn-contact">
          <Check size={14} /> CV requested — link sent to {req.data.sentTo}
        </p>
        <button
          type="button"
          className="rn-btn ghost"
          onClick={() =>
            void navigator.clipboard?.writeText(link).then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1800);
            })
          }
        >
          <Copy size={13} aria-hidden="true" /> {copied ? "Copied" : "Copy link"}
        </button>
      </div>
    );
  }
  return (
    <div className="rn-cv-requested">
      <p className="rn-contact rn-muted">
        <FileText size={14} /> No CV on file yet
      </p>
      <button type="button" className="rn-btn" onClick={() => req.mutate()} disabled={req.isPending}>
        <Mail size={13} aria-hidden="true" /> {req.isPending ? "Sending…" : "Request CV"}
      </button>
      {req.isError && (
        <p className="rn-muted sm">
          {req.error instanceof ApiError ? req.error.message : "Couldn't send the request — try again."}
        </p>
      )}
    </div>
  );
}

/**
 * A resume that was scanned but never became a candidate on this requirement has no candidacy row,
 * so details, team status, notes and the log have nothing to attach to. Say so, instead of hiding.
 */
function NotACandidateYet({ what }: { what: string }): ReactNode {
  return (
    <p className="rn-muted">
      This resume isn't a candidate on this requirement yet — add or invite them to record {what}.
    </p>
  );
}

function NotesTab({ candidacyId }: { candidacyId: string }): ReactNode {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const notes = useQuery({
    queryKey: ["notes", candidacyId],
    queryFn: () => api.get<{ notes: CandidacyNote[] }>(`/candidacies/${candidacyId}/notes`),
  });
  const add = useMutation({
    mutationFn: () => api.post(`/candidacies/${candidacyId}/notes`, { body: body.trim() }),
    onSuccess: () => {
      setBody("");
      void qc.invalidateQueries({ queryKey: ["notes", candidacyId] });
    },
  });
  return (
    <>
      <section className="rn-note-form">
        <textarea
          value={body}
          maxLength={4000}
          rows={3}
          placeholder="Add a note for your team — call summary, availability, anything worth remembering"
          onChange={(e) => setBody(e.target.value)}
        />
        <div>
          {add.isError && <span className="rn-muted sm">Couldn’t save the note.</span>}
          <button type="button" className="rn-btn" disabled={!body.trim() || add.isPending} onClick={() => add.mutate()}>
            {add.isPending ? "Saving…" : "Add note"}
          </button>
        </div>
      </section>
      <section>
        {notes.isLoading ? (
          <p className="rn-muted">Loading notes…</p>
        ) : notes.isError ? (
          <p className="rn-muted">Couldn’t load notes.</p>
        ) : notes.data?.notes.length ? (
          <ul className="rn-notes">
            {notes.data.notes.map((n) => (
              <li key={n.id}>
                <p>{n.body}</p>
                <small>
                  {n.authorName ?? "Team member"} · {new Date(n.createdAt).toLocaleString()}
                </small>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rn-muted">No notes yet.</p>
        )}
      </section>
    </>
  );
}

export function CandidateDetail({
  c,
  req,
  onClose,
}: {
  c: NetCandidate;
  req: NetRequisition;
  onClose: () => void;
}): ReactNode {
  const [tab, setTab] = useState<Tab>("overview");
  const [logOpen, setLogOpen] = useState(false);
  const comps = c.scan?.components.filter((x) => x.score != null) ?? [];
  const reached = STAGES.filter((s) => inStage(c, s.key as StageKey));

  return (
    <aside className="rn-detail" aria-label={`${c.name} — details`}>
      {/* Row 1: who · Row 2: status + team-status dropdown · Row 3: score rings. Each on its own
          row so nothing squeezes the name at the panel's ~390px width. */}
      <header className="rn-detail-head t1-cd-head">
        <div className="t1-cd-row1">
          <span className="rn-avatar lg">{initialsOf(c.name, "?").slice(0, 2)}</span>
          <div className="rn-detail-name">
            <strong>{c.name}</strong>
            <span>
              {displayTitle(req.title)}
              {c.years != null ? ` · ${c.years} yrs` : ""}
            </span>
          </div>
          <button
            type="button"
            className="rn-icon-btn"
            onClick={onClose}
            aria-label="Close candidate details"
            data-tip="Close"
            data-tip-pos="left"
          >
            <X size={16} />
          </button>
        </div>
        <div className="rn-pills t1-cd-row2">
          <StatusBadge status={c.status} withLabel />
          <StatusControl c={c} reqId={req.id} />
          <button
            type="button"
            className="rn-log-btn"
            onClick={() => setLogOpen(true)}
            disabled={!c.candidacyId}
            data-tip={c.candidacyId ? "Every step for this candidate, with date and time" : "Not a candidate on this requirement yet — nothing logged"}
            data-tip-pos="left"
          >
            <ScrollText size={14} aria-hidden="true" /> Activity log
          </button>
        </div>
        {/* Interview result first whenever there is one — it's the fuller signal — with resume fit
            beside it; before the interview, resume fit is the only score there is. */}
        <div className="t1-cd-scores">
          {c.interviewScore != null && (
            <span className="t1-cd-score" aria-label={`Interview score ${Math.round(c.interviewScore)}%`}>
              <ScoreRing value={c.interviewScore} size={40} />
              <span><b>Interview</b><small>Question-level score</small></span>
            </span>
          )}
          {c.competencyScore != null && (
            <span className="t1-cd-score" aria-label={`Competency score ${Math.round(c.competencyScore)}%`}>
              <ScoreRing value={c.competencyScore} size={40} />
              <span><b>Competency</b><small>Capability on the criteria</small></span>
            </span>
          )}
          <span className="t1-cd-score" aria-label={c.match != null ? `Resume fit ${Math.round(c.match)}%` : "Resume not scored yet"}>
            <ScoreRing value={c.match} size={40} />
            <span><b>Resume</b><small>Fit against this requirement</small></span>
          </span>
        </div>
      </header>

      <nav className="rn-tabs" role="tablist">
        {(
          [
            // Notes always shows — a scan-only resume explains itself inside it rather than the tab
            // silently vanishing (which read as "Notes was removed"). The activity log is NOT a tab:
            // a run-log needs width, so it opens in its own window from the header button.
            ["overview", "Overview"],
            ["analysis", "AI analysis"],
            ["interview", "Interview"],
            ["notes", "Notes"],
          ] as [Tab, string][]
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            className={tab === k ? "is-on" : ""}
            onClick={() => setTab(k)}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="rn-detail-body">
        {tab === "overview" && (
          <>
            <section>
              <h4>Contact information</h4>
              <p className="rn-contact">
                <Mail size={14} /> {c.email ?? "No email on file"}
              </p>
              {c.scan ? (
                <ResumeLink reqId={req.id} scanId={c.scan.id} filename={c.scan.filename} />
              ) : c.candidacyId ? (
                <RequestCv candidacyId={c.candidacyId} email={c.email} />
              ) : null}
            </section>
            {hasResumeProfile(c.scan?.profile) && (
              <section>
                <h4>From the resume</h4>
                <ResumeProfileLines profile={c.scan!.profile!} />
              </section>
            )}
            <section>
              <h4>Requirement must-haves</h4>
              {c.skills.length || c.missing.length ? (
                <div className="rn-chips">
                  {c.skills.map((s) => <span key={s} className="rn-chip on">{s}</span>)}
                  {c.missing.map((s) => (
                    <span key={s} className="rn-chip miss" title="Not evidenced in the resume">{s}</span>
                  ))}
                </div>
              ) : (
                <p className="rn-muted">No structured must-have check for this resume.</p>
              )}
            </section>
            {c.candidacyId ? (
              <CandidateDetailsFields candidacyId={c.candidacyId} reqId={req.id} />
            ) : (
              <section>
                <h4>Candidate details</h4>
                <NotACandidateYet what="notice period, CTC, expected CTC and a team status" />
              </section>
            )}
            <section className="rn-facts">
              <div><span>Experience</span><b>{c.years != null ? `${c.years} yrs` : "—"}</b></div>
              <div><span>Status</span><b>{STATE_LABEL[c.state ?? ""] ?? (c.match == null ? "Awaiting scan" : "Resume scored")}</b></div>
              <div><span>Resume fit</span><b>{c.match != null ? `${Math.round(c.match)}%` : "—"}</b></div>
              <div><span>Interview</span><b>{c.interviewScore != null ? `${Math.round(c.interviewScore)}%` : "—"}</b></div>
              <div><span>Competency</span><b>{c.competencyScore != null ? `${Math.round(c.competencyScore)}%` : "—"}</b></div>
            </section>
            <section>
              <h4>Recruitment journey</h4>
              <ol className="rn-journey">
                {STAGES.map((s) => {
                  const done = reached.some((r) => r.key === s.key);
                  return (
                    <li key={s.key} className={done ? "is-done" : ""}>
                      <i />
                      <span>{s.label}</span>
                      <small>{done ? "Reached" : "Not yet"}</small>
                    </li>
                  );
                })}
              </ol>
            </section>
          </>
        )}

        {tab === "analysis" && (
          <>
            {comps.length ? (
              <section className="rn-analysis">
                {comps.map((x) => (
                  <div key={x.name} className="rn-bar-row">
                    <span>{x.name}</span>
                    <b>{Math.round(x.score!)}%</b>
                    <i><em className={`tone-${scoreTone(x.score)}`} style={{ width: `${x.score}%` }} /></i>
                    {x.note && <p>{x.note}</p>}
                  </div>
                ))}
              </section>
            ) : (
              <p className="rn-muted">This resume has no AI breakdown yet.</p>
            )}
            {c.scan?.summary && (
              <section>
                <h4>Summary</h4>
                <p>{c.scan.summary}</p>
              </section>
            )}
            {c.scan?.rubric?.facts.flags.length ? (
              <section>
                <h4>Flags</h4>
                {c.scan.rubric.facts.flags.map((f) => (
                  <p key={f} className="rn-flag"><AlertTriangle size={13} /> {f}</p>
                ))}
              </section>
            ) : null}
            <p className="rn-muted sm">
              The AI ranks resumes against this requirement. A person makes every hiring decision.
            </p>
          </>
        )}

        {tab === "notes" &&
          (c.candidacyId ? <NotesTab candidacyId={c.candidacyId} /> : <NotACandidateYet what="notes for your team" />)}

        {tab === "interview" && (
          <>
            {c.candidacyId ? (
              <section className="rn-interview">
                <ScoreRing value={c.interviewScore} size={64} />
                <div>
                  <strong>{STATE_LABEL[c.state ?? ""] ?? "In pipeline"}</strong>
                  <p className="rn-muted">
                    {c.interviewScore != null
                      ? "Interview scored. The full report, recording and integrity checks are on the review page."
                      : "No interview score yet."}
                  </p>
                  {c.band && <span className={`rn-chip band-${c.band}`}>Authenticity: {c.band}</span>}
                </div>
              </section>
            ) : (
              <p className="rn-muted">
                Not invited to an interview yet. Choose who to interview from the requirement page.
              </p>
            )}
          </>
        )}
      </div>

      <footer className="rn-detail-foot">
        {c.candidacyId ? (
          <Link className="rn-cta" to={`/app/review/${c.candidacyId}`}>
            View report <ArrowRight size={15} />
          </Link>
        ) : (
          // A CV nobody has invited or added has no review page yet: give it a candidacy (no invite,
          // no email, no charge) and open its report.
          <ViewReportButton reqId={req.id} scanId={c.scan?.id ?? null} />
        )}
      </footer>
      {logOpen && c.candidacyId && (
        <Modal title={`Activity log — ${c.name}`} onClose={() => setLogOpen(false)} maxWidth={880}>
          <p className="mut sm" style={{ margin: "-4px 0 14px" }}>
            {displayTitle(req.title)}
            {c.email ? ` · ${c.email}` : ""}
          </p>
          <CandidateTimeline candidacyId={c.candidacyId} />
        </Modal>
      )}
    </aside>
  );
}
